// noinspection ES6UnusedImports
import * as bootstrap from 'bootstrap';

import {RouterLink, RouterModule, Routes} from '@angular/router';
import {BrowserModule} from '@angular/platform-browser';
import {ErrorHandler, Injector, NgModule} from '@angular/core';
import {VisitDetailsComponent} from './visit-details/visit-details.component';
import {
  CommonModule,
  CurrencyPipe,
  DecimalPipe,
  NgOptimizedImage,
  registerLocaleData,
  TitleCasePipe
} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import localeNL from '@angular/common/locales/nl'

import {AppComponent} from './app.component';
import {VisitComponent} from './visit-details/visit/visit.component';
import {NewVisitComponent} from './new-visit/new-visit.component';
import {NewsComponent} from './news/news.component';
import {PreferencesComponent} from './preferences/preferences.component';
import {SecurityComponent} from './visit-details/security/security.component';
import {WasteComponent} from './visit-details/waste/waste.component';
import {PassengersComponent} from './visit-details/passengers/passengers.component';
import {PassengerSummaryComponent} from './visit-details/passengers/summary/passenger-summary.component';
import {ShipStoresComponent} from './visit-details/ship-stores/ship-stores.component';
import {AuthoriseComponent} from './visit-details/authorise/authorise.component';
import {FinancialDeclarantComponent} from './visit-details/financial-declarant/financial-declarant.component';
import {InjectorProvider} from './common/injector-provider';
import {RefdataQueryHandlerMock} from './mocking/refdata-query-handler.mock';
import {BezoekschipQueryHandlerMock} from './mocking/bezoekschip-query-handler.mock';
import {FacetsQueryHandlerMock} from './mocking/facets-query-handler.mock';
import {UserHandlerMock} from './mocking/user-handler.mock';
import {QueryGateway} from './common/query-gateway';
import {CommandGateway} from './common/command-gateway';
import {EventGateway} from './common/event-gateway';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {NextNextPortsComponent, NextPortsComponent} from './visit-details/visit/next-ports/next-ports.component';
import PreviousPortsComponent from './visit-details/visit/previous-ports/previous-ports.component';
import {EditMovementComponent} from './visit-details/visit/edit-movement/edit-movement.component';
import {
  EditNauticalServiceComponent
} from './visit-details/visit/locations/edit-berth/edit-nautical-service/edit-nautical-service.component';
import {TimestampPipe} from './common/date/timestamp.pipe';
import {EtaPortAisComponent} from './visit-details/visit/eta-port-ais/eta-port-ais.component';
import {EditBerthComponent} from './refdata/edit-berth/edit-berth.component';
import {EditExemptionComponent} from "./refdata/edit-exemption/edit-exemption.component";
import {environment} from '../environments/environment';
import {BezoekschipCommandHandlerMock} from './mocking/bezoekschip-command-handler.mock';

import {HardcodedRefdataQueryHandler} from './refdata/hardcoded-refdata-query-handler';
import {
  ConfirmationOfPurchaseComponent
} from './visit-details/confirmation-of-purchase/confirmation-of-purchase.component';
import {
  NotificationOfDepartureComponent
} from './visit-details/notification-of-departure/notification-of-departure.component';
import {DeclarationStatusComponent} from './visit-details/visit-status/declaration-status/declaration-status.component';
import {ClearanceComponent} from './visit-details/visit/clearance/clearance.component';
import {ClearanceStatusComponent} from './visit-details/visit-status/clearance-status/clearance-status.component';
import {CookieModule} from 'ngx-cookie';
import {VisitStatusComponent} from './visit-details/visit-status/visit-status.component';
import {SentryErrorHandler} from './sentry-error-handler';
import {VisitAdminComponent} from './visit-details/visit/visit-admin/visit-admin.component';
import {PassengersAdminComponent} from './visit-details/passengers/passengers-admin/passengers-admin.component';
import {ShipStoresAdminComponent} from './visit-details/ship-stores/ship-stores-admin/ship-stores-admin.component';
import {SecurityAdminComponent} from './visit-details/security/security-admin/security-admin.component';
import {WasteAdminComponent} from './visit-details/waste/waste-admin/waste-admin.component';
import {ReportIssueComponent} from './report-issue/report-issue.component';
import {RegisterAtaComponent} from './visit-details/visit/locations/edit-berth/register-ata/register-ata.component';
import {RegisterAtdComponent} from './visit-details/visit/locations/edit-berth/register-atd/register-atd.component';
import {MessageOverviewComponent} from './message-overview/message-overview.component';
import {VesselDetailsComponent} from './visit-details/vessel-details/vessel-details.component';
import {RefdataComponent} from './refdata/refdata.component';
import {RefdataUpdateComponent} from './refdata/refdata-update/refdata-update.component';
import {EditPortComponent} from './refdata/edit-port/edit-port.component';
import {EditPortOfCallComponent} from "./refdata/edit-port-of-call/edit-port-of-call.component";
import {EditPortFacilityComponent} from './refdata/edit-port-facility/edit-port-facility.component';
import {EditCarrierComponent} from './refdata/edit-carrier/edit-carrier.component';
import {EditVesselComponent} from './refdata/edit-vessel/edit-vessel.component';
import {EditPilotExemptionComponent} from './refdata/edit-pilot-exemption/edit-pilot-exemption.component';
import {EditDangerousGoodComponent} from './refdata/edit-dangerous-good/edit-dangerous-good.component';
import {
  EditContainerGoodComponent
} from './refdata/edit-dangerous-good/edit-container-good/edit-container-good.component';
import {EditSolidGoodComponent} from './refdata/edit-dangerous-good/edit-solid-good/edit-solid-good.component';
import {EditOilComponent} from './refdata/edit-dangerous-good/edit-oil/edit-oil.component';
import {EditGasComponent} from './refdata/edit-dangerous-good/edit-gas/edit-gas.component';
import {EditChemicalComponent} from './refdata/edit-dangerous-good/edit-chemical/edit-chemical.component';
import {EditLinerServiceComponent} from './refdata/edit-liner-service/edit-liner-service.component';
import {EditLocationComponent} from './refdata/edit-location/edit-location.component';
import {EncryptComponent} from "./admin/encrypt/encrypt.component";
import {DangerousGoodsComponent} from './visit-details/dangerous-goods/dangerous-goods.component';
import {StowagesComponent} from './visit-details/dangerous-goods/stowages/stowages.component';
import {HandlingsComponent} from './visit-details/dangerous-goods/handlings/handlings.component';
import {
  EditDischargeComponent
} from './visit-details/dangerous-goods/handlings/edit-discharge/edit-discharge.component';
import {EditLoadingComponent} from './visit-details/dangerous-goods/handlings/edit-loading/edit-loading.component';
import {EditWashingComponent} from './visit-details/dangerous-goods/handlings/edit-washing/edit-washing.component';
import {EditInertingComponent} from './visit-details/dangerous-goods/handlings/edit-inerting/edit-inerting.component';
import {EditStowageComponent} from './visit-details/dangerous-goods/stowages/edit-stowage/edit-stowage.component';
import {FumigationDetailsComponent} from './visit-details/dangerous-goods/stowages/edit-stowage/fumigation-details/fumigation-details.component';
import {DefumigatorCompanyComponent} from './visit-details/dangerous-goods/stowages/edit-stowage/fumigation-details/defumigator-company/defumigator-company.component';
import {FumigationStevedoreComponent} from './visit-details/dangerous-goods/stowages/edit-stowage/fumigation-details/stevedore-company/fumigation-stevedore.component';
import {
  DangerousGoodsOverviewComponent
} from './visit-details/dangerous-goods/dangerous-goods-overview/dangerous-goods-overview.component';
import {
  DangerousGoodsAdminComponent
} from './visit-details/dangerous-goods/dangerous-goods-admin/dangerous-goods-admin.component';
import {EditBlendingComponent} from './visit-details/dangerous-goods/handlings/edit-blending/edit-blending.component';
import {
  EditVentilationComponent
} from './visit-details/dangerous-goods/handlings/edit-ventilation/edit-ventilation.component';
import {VisitHistoryComponent} from './visit-details/visit-history/visit-history.component';
import {RegisterAtdPortComponent} from './visit-details/visit/register-atd-port/register-atd-port.component';
import {EditRestowComponent} from './visit-details/dangerous-goods/handlings/edit-restow/edit-restow.component';
import {
  StormPilotageComponent
} from './visit-details/visit/locations/edit-berth/storm-pilotage/storm-pilotage.component';
import {PortEntryComponent} from './visit-details/visit/port-entry-tab/port-entry/port-entry.component';
import {UserMenuComponent} from './navigation/user-menu/user-menu.component';
import {CargoImportComponent} from './visit-details/cargo-import/cargo-import.component';
import {
  ImportManifestAdminComponent
} from './visit-details/cargo-import/import-declaration/consignments/manifest-admin/import-manifest-admin.component';
import {PartyComponent} from './visit-details/cargo-import/common/party/party.component';
import {
  WarehouseLicenseComponent
} from './visit-details/cargo-import/common/warehouse-license/warehouse-license.component';
import {TransitGuaranteeComponent} from './transit/transit-guarantees/transit-guarantee/transit-guarantee.component';
import {
  ConsignmentDetailsComponent
} from './visit-details/cargo-import/import-declaration/consignment-details/consignment-details.component';
import {
  GoodsPlacementComponent
} from './visit-details/cargo-import/import-declaration/goods-placement/goods-placement.component';
import {GoodsComponent} from './visit-details/cargo-import/import-declaration/goods/goods.component';
import {
  GoodDetailsComponent
} from './visit-details/cargo-import/import-declaration/good-details/good-details.component';
import {
  GoodsDocumentsComponent
} from './visit-details/cargo-import/import-declaration/goods-documents/goods-documents.component';
import {
  ConsignmentsComponent
} from './visit-details/cargo-import/import-declaration/consignments/consignments.component';
import {
  ImportContainerComponent
} from './visit-details/cargo-import/common/import-container/import-container.component';
import {ContainersComponent} from './visit-details/cargo-import/import-declaration/containers/containers.component';
import {
  OnCarriageDetailsComponent
} from './visit-details/cargo-import/import-declaration/on-carriage-details/on-carriage-details.component';
import {ImportDeclarationComponent} from './visit-details/cargo-import/import-declaration/import-declaration.component';
import {DischargeListComponent} from './visit-details/cargo-import/discharge-list/discharge-list.component';
import {
  DischargeListAdminComponent
} from './visit-details/cargo-import/discharge-list/discharge-list-admin/discharge-list-admin.component';
import {CargoSummaryComponent} from './visit-details/cargo-import/common/cargo-summary/cargo-summary.component';
import {AddressBookComponent} from './visit-details/cargo-import/address-book/address-book.component';
import {LicenseBookComponent} from './visit-details/cargo-import/license-book/license-book.component';
import {
  PortEntryWarningsComponent
} from './visit-details/visit/port-entry-tab/port-entry/port-entry-warnings/port-entry-warnings.component';
import {OverlandersComponent} from './visit-details/cargo-import/import-declaration/overlanders/overlanders.component';
import {EditTerminalComponent} from './refdata/edit-terminal/edit-terminal.component';
import {EditQuayComponent} from './refdata/edit-quay/edit-quay.component';
import {DepartureVoyageComponent} from './visit-details/visit/departure-voyage/departure-voyage.component';
import {ArrivalVoyageComponent} from './visit-details/visit/arrival-voyage/arrival-voyage.component';
import {
  ExitPointComponent,
  NextExitPointComponent
} from './visit-details/visit/locations/exit-point/exit-point.component';
import {ModalErrorAutofocus} from "./common/modal-error/modal-error.component";
import {EntryPointComponent} from './visit-details/visit/port-entry-tab/entry-point/entry-point.component';
import {
  ConsignmentsAdminComponent
} from './visit-details/cargo-import/import-declaration/consignments/consignments-admin/consignments-admin.component';
import {
  CargoFilterComponent
} from './visit-details/cargo-import/import-declaration/import-filter/cargo-filter.component';
import {TidalWindowComponent} from './visit-details/visit/tidal-window/tidal-window.component';
import {FormattedDateRangeComponent} from './common/date/formatted-date-range/formatted-date-range.component';
import {HistoryRibbonComponent} from './visit-details/ribbon/history-ribbon.component';
import {CollectorSummaryComponent} from './waste-collector/collector-summary/collector-summary.component';
import {CollectorOverviewComponent} from './waste-collector/collector-overview/collector-overview.component';
import {
  WasteCollectionPdfComponent
} from './waste-collector/waste-collection/waste-collection-pdf/waste-collection-pdf.component';
import {WasteCollectionComponent} from './waste-collector/waste-collection/waste-collection.component';
import {TimelineComponent} from './visit-details/timeline/timeline.component';
import {DefectsComponent} from './visit-details/visit/defects/defects.component';
import {InspectionsComponent} from './visit-details/visit/inspections/inspections.component';
import {
  PilotBoardingPlaceComponent
} from './visit-details/visit/port-entry-tab/port-entry/pilot-boarding-place/pilot-boarding-place.component';
import {OrderStatusComponent} from './visit-details/visit/order-status/order-status.component';
import {SelectBerthComponent} from './visit-details/visit/select-berth/select-berth.component';
import {AgentTransferComponent} from './visit-details/visit/agent-transfer/agent-transfer.component';
import {AddRemoveBerthComponent} from './visit-details/visit/add-remove-berth/add-remove-berth.component';
import {
  EntryDependencyIconComponent
} from './visit-details/visit/entry-dependency-icon/entry-dependency-icon.component';
import {
  EarliestTimePortEntryComponent
} from './visit-details/visit/port-entry-tab/port-entry/earliest-time-port-entry/earliest-time-port-entry.component';
import {PortEntryTabComponent} from './visit-details/visit/port-entry-tab/port-entry-tab.component';
import {
  PortEntryCreateComponent
} from './visit-details/visit/port-entry-tab/port-entry-create/port-entry-create.component';
import {PilotStationComponent} from './visit-details/visit/port-entry-tab/pilot-station/pilot-station.component';
import {MovementComponent} from './visit-details/visit/movement/movement.component';
import {BerthVisitComponent} from './visit-details/visit/berth-visit/berth-visit.component';
import {PlanningHistoryComponent} from './visit-details/visit/planning-history/planning-history.component';
import {PortArrivalComponent} from './visit-details/visit/port-arrival/port-arrival.component';
import {PortDepartureComponent} from './visit-details/visit/port-departure/port-departure.component';
import {
  BerthVisitDetailsComponent
} from './visit-details/visit/berth-visit/berth-visit-details/berth-visit-details.component';
import {
  ShorePowerComponent
} from './visit-details/visit/berth-visit/berth-visit-details/shore-power/shore-power.component';
import {
  ShorePowerDetailsComponent
} from './visit-details/visit/berth-visit/berth-visit-details/shore-power/shore-power-details/shore-power-details.component';
import {
  BerthVisitTimesComponent
} from './visit-details/visit/berth-visit/berth-visit-details/berth-visit-times.component';
import {
  BoatmenToBerthSummaryComponent
} from './visit-details/visit/boatmen-to-berth-summary/boatmen-to-berth-summary.component';
import {TugboatsSummaryComponent} from './visit-details/visit/tugboats-summary/tugboats-summary.component';
import {NauticalServicesComponent} from './visit-details/visit/nautical-services/nautical-services.component';
import {MovementSummaryComponent} from './visit-details/visit/movement-summary/movement-summary.component';
import {
  MovementDurationSummaryComponent
} from './visit-details/visit/movement-duration-summary/movement-duration-summary.component';
import {
  CargoImportAdminComponent
} from './visit-details/cargo-import/import-declaration/admin/cargo-import-admin.component';
import {EditSizeTypeComponent} from './refdata/edit-sizetype/edit-sizetype.component';
import {EditTerminalEmailComponent} from './refdata/edit-terminal-email/edit-terminal-email.component';
import {EditDangerInformationComponent} from './refdata/edit-dangerous-good-cargo/edit-danger-information.component';
import {HistoryComponent} from './history/history.component';
import {ActionNavBarComponent} from './visit-details/action-nav-bar/action-nav-bar.component';
import {ExchangeAtBerthComponent} from './visit-details/visit/exchange-at-berth/exchange-at-berth.component';
import {HealthDeclarationComponent} from './visit-details/health-declaration/health-declaration.component';
import {CargoLoadingComponent} from './visit-details/cargo-loading/cargo-loading.component';
import {LoadListComponent} from './visit-details/cargo-loading/load-list/load-list.component';
import {
  LoadingOverlandersComponent
} from './visit-details/cargo-loading/loading-overlanders/loading-overlanders.component';
import {LoadContainerComponent} from './visit-details/cargo-loading/load-container/load-container.component';
import {
  NauticalServicesInboundComponent
} from './visit-details/visit/nautical-services-inbound/nautical-services-inbound.component';
import {EtaEuEntryComponent} from './visit-details/visit/eta-eu-entry/eta-eu-entry.component';
import {
  HealthDeclarationAdminComponent
} from './visit-details/health-declaration/health-declaration-admin/health-declaration-admin.component';
import {
  MovementBetweenBerthsComponent
} from './visit-details/visit/movement-between-berths/movement-between-berths.component';
import {TimelineDateTimeComponent} from './visit-details/visit/timeline-date-time/timeline-date-time.component';
import {TimelineIconComponent} from './visit-details/visit/timeline-icon/timeline-icon.component';
import {
  DeclarationStatusHealthComponent
} from './visit-details/visit-status/declaration-status-health/declaration-status-health.component';
import {
  CargoImportOverviewComponent
} from './visit-details/cargo-import/cargo-import-overview/cargo-import-overview.component';
import {GeneralInformationComponent} from './visit-details/visit/general-information/general-information.component';
import {WasteAtBerthIconComponent} from './visit-details/visit/waste-at-berth-icon/waste-at-berth-icon.component';

import {TransitGuaranteesComponent} from './transit/transit-guarantees/transit-guarantees.component';
import {TransitOverviewComponent} from './transit/transit-overview.component';
import {TransitPaginationComponent} from './transit/transit-pagination/transit-pagination.component';
import {ItineraryComponent} from './waste-collector/collector-summary/itinerary/itinerary.component';
import {
  CollectorSummaryTitleBarComponent
} from './waste-collector/collector-summary/collector-summary-title-bar/collector-summary-title-bar.component';
import {TransitSummaryComponent} from "./transit/transit-summary/transit-summary.component";
import {TransitDeclarationComponent} from "./transit/transit-declaration/transit-declaration.component";
import {
  TransitGoodComponent
} from "./transit/transit-declaration/transit-goods/transit-goods-item/transit-good.component";
import {TransitDetailsComponent} from "./transit/transit-declaration/transit-details/transit-details.component";
import {TransitGoodsComponent} from "./transit/transit-declaration/transit-goods/transit-goods.component";
import {
  ShipToShipActivitiesComponent
} from "./visit-details/security/ship-to-ship-activities/ship-to-ship-activities.component";
import {TransitHistoryComponent} from "./transit/transit-declaration/transit-history/transit-history.component";
import {TransitFilterComponent} from "./transit/transit-filter/transit-filter.component";
import {GuaranteeCommandHandlerMock} from "./mocking/guarantee-command-handler.mock";
import {
  GuaranteeAdminComponent
} from "./transit/transit-guarantees/transit-guarantee/guarantee-admin/guarantee-admin.component";
import {FluxSearchImportComponent} from './refdata/flux-search-import/flux-search-import.component';
import {TransitTemplatesComponent} from "./transit/transit-templates/transit-templates.component";
import {TransitTemplateComponent} from "./transit/transit-templates/transit-template/transit-template.component";
import {
  TransitDeclarationAdminComponent
} from "./transit/transit-declaration/transit-declaration-admin/transit-declaration-admin.component";
import {
  TransitProofOfArrivalComponent
} from "./transit/transit-declaration/transit-proof-of-arrival/transit-proof-of-arrival.component";
import {
  TransitCopyContainersFromConsignmentsComponent
} from "./transit/transit-copy-from-consignments/transit-copy-containers-from-consignments/transit-copy-containers-from-consignments.component";
import {TransitPartyComponent} from "./transit/transit-address-book/transit-party/transit-party.component";
import {TransitAddressBookComponent} from "./transit/transit-address-book/transit-address-book.component";
import {FluxSearchExportComponent} from "./refdata/flux-search-export/flux-search-export.component";
import {
  PreviousPortsAndFacilitiesComponent
} from "./visit-details/security/previous-ports-and-facilities/previous-ports-and-facilities.component";
import {TravelTimesQueryHandler} from "./refdata/travel-times-query-handler";
import {CommercialReleaseOverviewComponent} from "./commercial-release/commercial-release-overview.component";
import {
  CommercialReleaseDashboardComponent
} from "./commercial-release/commercial-release-dashboard/commercial-release-dashboard.component";
import {
  CommercialReleaseDeclarationComponent
} from "./commercial-release/commercial-release-declaration/commercial-release-declaration.component";
import {
  CommercialReleaseDetailsComponent
} from "./commercial-release/commercial-release-declaration/commercial-release-details/commercial-release-details.component";
import {
  CommercialReleaseFilterComponent
} from "./commercial-release/commercial-release-filter/commercial-release-filter.component";
import {
  CommercialReleaseAdminComponent
} from "./commercial-release/commercial-release-declaration/commercial-release-admin/commercial-release-admin.component";
import {
  CommercialReleaseSummaryComponent
} from "./commercial-release/commercial-release-summary/commercial-release-summary.component";
import {MidOverviewComponent} from './transit/mid-overview/mid-overview.component';
import {
  TransitDeclarationSendTriggerInfoComponent
} from "./transit/transit-declaration-send-trigger-info/transit-declaration-send-trigger-info.component";
import {
  BatchTransitDeclarationComponent
} from './transit/batch-transit-declaration/batch-transit-declaration.component';
import {
  TransitDeclarationsFromContainersComponent
} from './transit/transit-copy-from-consignments/transit-declarations-from-containers/transit-declarations-from-containers.component';
import {
  TransitCopyBulkFromConsignmentsComponent
} from "./transit/transit-copy-from-consignments/transit-copy-bulk-from-consignments/transit-copy-bulk-from-consignments.component";
import {ReleaseToPartyOverviewComponent} from './release-to-party-overview/release-to-party-overview.component';
import {ReleaseToPartyComponent} from './release-to-party-overview/release-to-party/release-to-party.component';
import {EditWasteMaterialComponent} from "./refdata/edit-waste-material/edit-waste-material.component";
import {
  TransitGuaranteeHistoryComponent
} from "./transit/transit-guarantees/transit-guarantee-history/transit-guarantee-history.component";
import {
  CommercialReleaseHistoryComponent
} from './commercial-release/commercial-release-declaration/commercial-release-history/commercial-release-history.component';
import {MaintenanceComponent} from './maintenance/maintenance.component';
import {AdminPanelComponent} from './admin/admin-panel/admin-panel.component';
import {FormComponent} from './common/form/form.component';
import {SspiClearanceComponent} from "./visit-details/visit/clearance/sspi.clearance.component";
import {EkhClearanceComponent} from "./visit-details/visit/clearance/ekh.clearance.component";
import {EmasClearanceComponent} from "./visit-details/visit/clearance/emas.clearance.component";
import {EmgsClearanceComponent} from "./visit-details/visit/clearance/emgs.clearance.component";
import {EditBunkerProductComponent} from "./refdata/edit-bunker-product/edit-bunker-product.component";
import {
  EditBunkerProductSupplierComponent
} from "./refdata/edit-bunker-product-supplier/edit-bunker-product-supplier.component";
import {
  TerminalPlanningAdminComponent
} from "./visit-details/visit/berth-visit/berth-visit-details/terminal-planning-admin/terminal-planning-admin.component";
import {
  RegisterTerminalPlanningComponent
} from "./visit-details/visit/berth-visit/berth-visit-details/terminal-planning-admin/register-terminal-planning/register-terminal-planning.component";
import {
  RegisterTerminalStatusComponent
} from "./visit-details/visit/berth-visit/berth-visit-details/terminal-planning-admin/register-terminal-status/register-terminal-status.component";
import {EditBunkerLicenseComponent} from "./refdata/edit-bunker-license/edit-bunker-license.component";
import {
  MultiBerthComponent
} from './visit-details/visit/berth-visit/berth-visit-details/multi-berth/multi-berth.component';
import {MetricsQueryHandlerMock} from "./mocking/metrics-query-handler.mock";
import {CommercialReleaseDashboardQueryHandlerMock} from "./mocking/commercial-release-dashboard-query-handler.mock";
import {EkhClearanceStatusComponent} from "./visit-details/visit/clearance/ekh-clearance-status.component";
import {
  DeclareTranshipmentOwnConsignmentComponent
} from "./transhipment/declare-transhipment/own-consignment/declare-transhipment-own-consignment.component";
import {
  DeclareTranshipmentContainerConsignmentComponent
} from "./transhipment/declare-transhipment/container-consignment/declare-transhipment-container-consignment.component";
import {
  DeclareTranshipmentBulkConsignmentComponent
} from "./transhipment/declare-transhipment/bulk-consignment/declare-transhipment-bulk-consignment.component";
import {TranshipmentFilterComponent} from "./transhipment/transhipment-filter/transhipment-filter.component";
import {TranshipmentOverviewComponent} from "./transhipment/transhipment-overview.component";
import {ViewTranshipmentComponent} from "./transhipment/view-transhipment/view-transhipment.component";
import {TranshipmentGoodDetailsComponent} from "./transhipment/good-details/transhipment-good-details.component";
import {
  TranshipmentGoodsPlacementComponent
} from "./transhipment/goods-placement/transhipment-goods-placement.component";
import {TranshipmentSummaryComponent} from "./transhipment/transhipment-summary/transhipment-summary.component";
import {TranshipmentHistoryComponent} from "./transhipment/transhipment-history/transhipment-history.component";
import {SystemLinkComponent} from './system-link/system-link.component';
import {MdohClearanceComponent} from "./visit-details/visit/clearance/mdoh.clearance.component";
import {
  PortPassagePlanningAreaComponent
} from "./visit-details/visit/port-entry-tab/port-entry/port-passage-planning-area/port-passage-planning-area.component";
import {ConfirmationModalComponent} from "./common/confirmation-modal/confirmation-modal.component";
import {TypeaheadWindowComponent} from "./common/typeahead/typeahead-window.component";
import {
  CommercialReleaseEmailPreferencesComponent
} from './preferences/commercial-release-email-preferences/commercial-release-email-preferences.component';
import {
  TerminalPlanningNumberInfoComponent
} from './visit-details/visit/berth-visit/berth-visit-details/terminal-planning-number-info/terminal-planning-number-info.component';
import {
  VisitDeclarationStatusComponent
} from './visit-details/visit-status/visit-declaration-status/visit-declaration-status.component';
import {PortVisitModule} from "./new-portvisit/port-visit.module";
import {AppContext} from "./app-context";
import {PortVisitCommonModule} from "./common/port-visit-common.module";
import {normalChildRouteGuard, normalRouteGuard} from "./normal-route.guard";
import {TranshipmentGoodsComponent} from "./transhipment/goods/transhipment-goods.component";
import {
  LabeledDateTimeComponent
} from "./visit-details/visit/berth-visit/berth-visit-details/labeled-date-time.component";
import {BunkeringEtcComponent} from "./visit-details/visit/berth-visit/berth-visit-details/bunkering-etc.component";
import {EditAddressComponent} from './refdata/edit-address/edit-address.component';
import {
  TerminalStatusAdminComponent
} from "./visit-details/visit/berth-visit/berth-visit-details/terminal-planning-admin/register-terminal-status/terminal-status-admin.component";
import {CalendarPipe} from "./common/date/calendar.pipe";
import {ExemptionsComponent} from "./visit-details/visit/exemptions/common/exemptions_component/exemptions.component";
import {
  ExemptionsVesselComponent
} from "./visit-details/visit/exemptions/common/exemptions_vessel/exemptions_vessel.component";
import {
  ExemptionsVesselBargeComponent
} from "./visit-details/visit/exemptions/common/exemptions_vessel/barge/exemptions_vessel_barge.component";
import {
  ExemptionsVesselShipComponent
} from "./visit-details/visit/exemptions/common/exemptions_vessel/ship/exemptions_vessel_ship.component";
import {ExemptionBaseComponent} from "./visit-details/visit/exemptions/common/exemption_base/exemption_base.component";
import {Exemption_3_2Component} from "./visit-details/visit/exemptions/types/exemption_3_2/exemption_3_2.component";
import {Exemption_3_4Component} from "./visit-details/visit/exemptions/types/exemption_3_4/exemption_3_4.component";
import {Exemption_3_5Component} from "./visit-details/visit/exemptions/types/exemption_3_5/exemption_3_5.component";
import {Exemption_3_6Component} from "./visit-details/visit/exemptions/types/exemption_3_6/exemption_3_6.component";
import {Exemption_3_11Component} from "./visit-details/visit/exemptions/types/exemption_3_11/exemption_3_11.component";
import {Exemption_3_13Component} from "./visit-details/visit/exemptions/types/exemption_3_13/exemption_3_13.component";
import {Exemption_4_1Component} from "./visit-details/visit/exemptions/types/exemption_4_1/exemption_4_1.component";
import {Exemption_4_5Component} from "./visit-details/visit/exemptions/types/exemption_4_5/exemption_4_5.component";
import {Exemption_4_6Component} from "./visit-details/visit/exemptions/types/exemption_4_6/exemption_4_6.component";
import {Exemption_4_11Component} from "./visit-details/visit/exemptions/types/exemption_4_11/exemption_4_11.component";
import {
  Exemption_4_11bComponent
} from "./visit-details/visit/exemptions/types/exemption_4_11b/exemption_4_11b.component";
import {
  Exemption_4_13_1Component
} from "./visit-details/visit/exemptions/types/exemption_4_13_1/exemption_4_13_1.component";
import {
  Exemption_4_13_2Component
} from "./visit-details/visit/exemptions/types/exemption_4_13_2/exemption_4_13_2.component";
import {Exemption_5_2Component} from "./visit-details/visit/exemptions/types/exemption_5_2/exemption_5_2.component";
import {Exemption_5_3Component} from "./visit-details/visit/exemptions/types/exemption_5_3/exemption_5_3.component";
import {Exemption_5_4Component} from "./visit-details/visit/exemptions/types/exemption_5_4/exemption_5_4.component";
import {
  Exemption_5_4_2Component
} from "./visit-details/visit/exemptions/types/exemption_5_4_2/exemption_5_4_2.component";
import {
  Exemption_6_3_1Component
} from "./visit-details/visit/exemptions/types/exemption_6_3_1/exemption_6_3_1.component";
import {
  Exemption_6_3_3Component
} from "./visit-details/visit/exemptions/types/exemption_6_3_3/exemption_6_3_3.component";
import {
  Exemption_6_3_5Component
} from "./visit-details/visit/exemptions/types/exemption_6_3_5/exemption_6_3_5.component";
import {Exemption_6_4Component} from "./visit-details/visit/exemptions/types/exemption_6_4/exemption_6_4.component";
import {Exemption_6_5Component} from "./visit-details/visit/exemptions/types/exemption_6_5/exemption_6_5.component";
import {
  Exemption_7_1_1Component
} from "./visit-details/visit/exemptions/types/exemption_7_1_1/exemption_7_1_1.component";
import {
  Exemption_7_1_2Component
} from "./visit-details/visit/exemptions/types/exemption_7_1_2/exemption_7_1_2.component";
import {Exemption_8_1Component} from "./visit-details/visit/exemptions/types/exemption_8_1/exemption_8_1.component";
import {
  ExemptionOudeMaasComponent
} from "./visit-details/visit/exemptions/types/exemption_oude-maas/exemption_oude-maas";
import {ExemptionGeneralComponent} from "./visit-details/visit/exemptions/types/exemption_general/exemption_general";
import {ExemptionIspsComponent} from "./visit-details/visit/exemptions/types/exemption_isps/exemption_isps.component";
import {
  ExemptionDebunkeringComponent
} from "./visit-details/visit/exemptions/types/exemption_debunkering/exemption_debunkering.component";
import {ExemptionTijpoortComponent} from "./visit-details/visit/exemptions/types/exemption_tijpoort/exemption_tijpoort";
import {sessionMonitoringConfig, SessionMonitoringConfig, SessionMonitoringModule} from "@portbase/session-monitoring";
import {AuthenticationTokenInterceptor} from "./authentication/authentication-token.interceptor";
import {HarbourDuesComponent} from "./visit-details/harbour-dues/harbour-dues.component";
import {
  HarbourDuesAdminComponent
} from "./visit-details/harbour-dues/rotterdam/details/harbour-dues-admin/harbour-dues-admin.component";
import {CdkDrag, CdkDragHandle, CdkDragPlaceholder, CdkDropList} from "@angular/cdk/drag-drop";
import {
  ServiceMonitoringComponent
} from './system-link/system-link-logged-in/service-monitoring/service-monitoring.component';
import {
  ServiceMonitoringChartComponent
} from './system-link/system-link-logged-in/service-monitoring/service-monitoring-chart/service-monitoring-chart.component';
import {
  SharedChartLegendComponent
} from "./system-link/system-link-logged-in/service-monitoring/shared-chart-legend/shared-chart-legend.component";
import {newRouteGuard} from "./new-portvisit/new-route.guard";
import {commercialReleaseRedirectGuard} from "./commercial-release/commercial-release-redirect.guard";
import {
  EditOrderDeadlinesComponent
} from "./refdata/edit-port-of-call/edit-order-deadlines/edit-order-deadlines.component";
import {
  EditPilotTransferPointComponent
} from './refdata/edit-port-of-call/edit-pilot-transfer-point/edit-pilot-transfer-point.component';
import {
  PilotTrajectoryComponent
} from './visit-details/visit/port-entry-tab/port-entry/pilot-trajectory/pilot-trajectory.component';
import {
  HelmsmenToBerthSummaryComponent
} from './visit-details/visit/helmsmen-to-berth-summary/helmsmen-to-berth-summary.component';
import {RequiredQuantityPipe} from './visit-details/visit/required-quantity/required-quantity.pipe';
import {NdjsonStatusComponent} from "./common/ndjson-status/ndjson-status.component";
import {NdjsonService} from "./common/ndjson-status/ndjson.service";
import {
  HarbourDuesHistoryComponent
} from "./visit-details/harbour-dues/rotterdam/history/harbour-dues-history.component";
import {RouteData} from "./route-data";
import {VisitOverviewComponent} from "./new-portvisit/visit-overview/visit-overview.component";
import {EmergencyOverviewComponent} from "./new-portvisit/emergency-overview/emergency-overview.component";
import {
  VisitOverviewItemComponent
} from "./new-portvisit/visit-overview/visit-overview-item/visit-overview-item.component";
import {
  EmergencyOverviewItemComponent
} from "./new-portvisit/emergency-overview/emergency-overview-item/emergency-overview-item.component";
import {
  EmergencyOverviewServiceStatusComponent
} from "./new-portvisit/emergency-overview/emergency-overview-item/emergency-overview-service-status/emergency-overview-service-status.component";
import {
  OperationalVisitEditorComponent
} from "./new-portvisit/emergency-overview/operational-visit-editor/operational-visit-editor.component";
import {
  OperationalPortAuthorityEditorComponent
} from "./new-portvisit/emergency-overview/operational-visit-editor/operational-port-authority-editor/operational-port-authority-editor.component";
import {
  OperationalClearanceFieldComponent
} from "./new-portvisit/emergency-overview/operational-visit-editor/operational-port-authority-editor/operational-clearance-field/operational-clearance-field.component";
import {
  OperationalServicesEditorComponent
} from "./new-portvisit/emergency-overview/operational-visit-editor/operational-services-editor/operational-services-editor.component";
import {
  OperationalBerthEditorComponent
} from "./new-portvisit/emergency-overview/operational-visit-editor/operational-berth-editor/operational-berth-editor.component";
import {
  VisitOverviewServiceStatusComponent
} from "./new-portvisit/visit-overview/visit-overview-item/visit-overview-service-status/visit-overview-service-status.component";
import {
  TerminalVisitOverviewServiceStatusComponent
} from "./new-portvisit/visit-overview/visit-overview-item/visit-overview-service-status/terminal-visit-overview-service-status/terminal-visit-overview-service-status.component";
import {PopoverComponent} from "./common/popover/popover.component";
import {HighlightComponent} from "./common/typeahead/highlight/highlight.component";
import {
  StevedoreVisitOverviewServiceStatusComponent
} from "./new-portvisit/visit-overview/visit-overview-item/visit-overview-service-status/stevedore-visit-overview-service-status/stevedore-visit-overview-service-status.component";
import {AppHandler} from "./new-portvisit/handlers/app-handler";
import {
  VesselCommunicationComponent
} from "./visit-details/visit/vessel-communication-details/vessel-communication-details.component";
import {InfoComponent} from "./visit-details/harbour-dues/rotterdam/details/info/info.component";
import {HarbourDuesHeaderComponent} from "./visit-details/harbour-dues/rotterdam/header/header.component";
import {
  ExemptionComponent
} from "./visit-details/harbour-dues/rotterdam/details/adjustments/exemption/exemption.component";
import {
  DiscountComponent
} from "./visit-details/harbour-dues/rotterdam/details/adjustments/discount/discount.component";
import {HarbourDuesAmsterdamComponent} from "./visit-details/harbour-dues/amsterdam/harbour-dues-amsterdam.component";
import {
  HarbourDuesHistoryAmsterdamComponent
} from "./visit-details/harbour-dues/amsterdam/history/harbour-dues-history-amsterdam.component";
import {
  HarbourDuesAdminAmsterdamComponent
} from "./visit-details/harbour-dues/amsterdam/harbour-dues-admin/harbour-dues-admin-amsterdam.component";
import {
  EquipmentListAmsterdamComponent
} from "./visit-details/harbour-dues/amsterdam/equipment-list/equipment-list-amsterdam.component";
import {
  EquipmentCategoryAmsterdamComponent
} from "./visit-details/harbour-dues/amsterdam/equipment-list/equipment-category/equipment-category-amsterdam.component";
import {DiscountAmsterdamComponent} from "./visit-details/harbour-dues/amsterdam/discount/discount-amsterdam.component";
import {
  CargoListAmsterdamComponent
} from "./visit-details/harbour-dues/amsterdam/cargo-list/cargo-list-amsterdam.component";
import {
  CargoWeightAmsterdamComponent
} from "./visit-details/harbour-dues/amsterdam/cargo-list/cargo-weight/cargo-weight-amsterdam.component";
import {HarbourDuesRotterdamComponent} from "./visit-details/harbour-dues/rotterdam/harbour-dues-rotterdam.component";
import {
  HarbourDuesDetailsRotterdamComponent
} from "./visit-details/harbour-dues/rotterdam/details/harbour-dues-rotterdam-details.component";
import {
  BerthDetailsRotterdamComponent
} from "./visit-details/harbour-dues/rotterdam/details/berth-details/berth-details-rotterdam.component";
import {
  BerthVisitRotterdamComponent
} from "./visit-details/harbour-dues/rotterdam/details/berth-details/berth-visit/berth-visit-rotterdam.component";
import {
  BerthVisitCargoRotterdamComponent
} from "./visit-details/harbour-dues/rotterdam/details/berth-details/berth-visit/cargo/berth-visit-cargo-rotterdam.component";
import {
  ContainerHandlingRotterdamComponent
} from "./visit-details/harbour-dues/rotterdam/berth-details/berth-visit/container/container-handling/container-handling-rotterdam.component";
import {
  BerthVisitContainerRotterdamComponent
} from "./visit-details/harbour-dues/rotterdam/berth-details/berth-visit/container/berth-visit-container-rotterdam.component";
import {
  BerthVisitTabsRotterdamComponent
} from "./visit-details/harbour-dues/rotterdam/berth-details/berth-visit/berth-visit-tabs/berth-visit-tabs-rotterdam.component";
import {
  HarbourDuesCostsRotterdamComponent
} from "./visit-details/harbour-dues/rotterdam/details/invoice/harbour-dues-rotterdam-costs.component";
import {
  HarbourDuesRotterdamProFormaComponent
} from "./visit-details/harbour-dues/rotterdam/details/pro-forma/harbour-dues-rotterdam-pro-forma.component";
import {
  HarbourDuesRotterdamProFormaInvoiceComponent
} from "./visit-details/harbour-dues/rotterdam/details/pro-forma/invoice/harbour-dues-rotterdam-pro-forma-invoice.component";
import {
  HarbourDuesRotterdamProFormaInvoiceSkeletonComponent
} from "./visit-details/harbour-dues/rotterdam/details/pro-forma/invoice-skeleton/harbour-dues-rotterdam-pro-forma-invoice-skeleton.component";
import {
  HarbourDuesRotterdamProFormaTableComponent
} from "./visit-details/harbour-dues/rotterdam/details/pro-forma/table/harbour-dues-rotterdam-pro-forma-table.component";
import {
  HarbourDuesRotterdamProFormaColumnDirective
} from "./visit-details/harbour-dues/rotterdam/details/pro-forma/table/harbour-dues-rotterdam-pro-forma-column.directive";
import {
  HarbourDuesRotterdamProFormaValueDirective
} from "./visit-details/harbour-dues/rotterdam/details/pro-forma/table/harbour-dues-rotterdam-pro-forma-value.directive";
import {
  ReleaseToPartyHistoryComponent
} from "./release-to-party-overview/release-to-party/release-to-party-history/release-to-party-history.component";
import {MovementIconsComponent} from "./new-portvisit/common/movement-icons/movement-icons.component";
import {
  EmergencyProcedureSettingsComponent
} from "./admin/emergencyprocedure/settings/emergency-procedure-settings.component";
import {OrganisationSettings} from "./admin/organisation-settings/organisation-settings";
import {
  HarbourDuesOverviewComponent
} from "./new-portvisit/harbour-dues-rotterdam-overview/harbour-dues-overview.component";
import {
  SpecialTariffComponent
} from "./visit-details/harbour-dues/rotterdam/details/adjustments/special-tariff/special-tariff.component";
import {
  IncidentalComponent
} from "./visit-details/harbour-dues/rotterdam/details/adjustments/incidental/harbour-dues-rotterdam-incidental.component";
import {
  ReductionRotterdamComponent
} from "./visit-details/harbour-dues/rotterdam/details/adjustments/reduction-rotterdam.component";

registerLocaleData(localeNL, 'nl');

registerLocaleData(localeNL);

export function getSessionMonitoringConfig(): SessionMonitoringConfig {
  const refreshUrl = AppContext.getRefreshUrl();

  return sessionMonitoringConfig({
    refreshUrl,
    prodMode: true,
  });
}

const appRoutes: Routes = [
  {
    path: '',
    canActivate: [normalRouteGuard],
    canActivateChild: [normalChildRouteGuard],
    children: [{
      path: 'details/:crn',
      component: VisitDetailsComponent,
      canActivateChild: [normalChildRouteGuard],
      children: [
        {path: 'visit', component: VisitComponent},
        {path: 'authorise', component: AuthoriseComponent},
        {path: 'harbourDues', component: HarbourDuesComponent, canActivate: [newRouteGuard]},
        {path: 'security', component: SecurityComponent},
        {path: 'passengers', component: PassengersComponent},
        {path: 'health', component: HealthDeclarationComponent},
        {path: 'waste', component: WasteComponent},
        {path: 'shipStores', component: ShipStoresComponent},
        {path: 'cargoImport', component: CargoImportComponent},
        {path: 'cargoImport/:shortName', component: CargoImportComponent},
        {path: 'cargoImport/:shortName/:filterTerm', component: CargoImportComponent},
        {path: 'cargoImportOverview', component: CargoImportOverviewComponent},
        {path: 'dangerousGoods', component: DangerousGoodsOverviewComponent},
        {path: 'dangerousGoods/:shortName', component: DangerousGoodsComponent},
        {path: 'transit', component: TransitOverviewComponent},
        {path: 'transhipment', component: TranshipmentOverviewComponent},
        {path: 'commercial-release', canActivate: [commercialReleaseRedirectGuard], component: CommercialReleaseOverviewComponent},
        {path: 'history', component: VisitHistoryComponent},
        {path: 'loading', component: CargoLoadingComponent},
        {path: '**', redirectTo: 'visit'}
      ]
    },
      {
        path: '',
        canActivate: [newRouteGuard],
        component: VisitOverviewComponent,
        data: <RouteData>{
          manualScrolling: true
        }
      },
      {
        path: 'emergency',
        canActivate: [newRouteGuard],
        component: EmergencyOverviewComponent,
        data: <RouteData>{
          manualScrolling: true
        }
      },
      {path: 'port-dues', component: HarbourDuesOverviewComponent},
      {path: 'newVisit', component: NewVisitComponent},
      {path: 'message-overview', component: MessageOverviewComponent},
      {path: 'news', component: NewsComponent},
      {path: 'preferences', component: PreferencesComponent},
      {path: 'refdata', component: RefdataComponent},
      {path: 'admin/panel', component: AdminPanelComponent},
      {path: 'admin/organisationSettings', component: OrganisationSettings},
      {path: 'admin/monitoring', component: ServiceMonitoringComponent},
      {path: 'encrypt', component: EncryptComponent},
      {path: 'waste', component: CollectorOverviewComponent},
      {path: 'transit', component: TransitOverviewComponent},
      {path: 'transhipment', component: TranshipmentOverviewComponent},
      {path: 'commercial-release', canActivate: [commercialReleaseRedirectGuard], component: CommercialReleaseOverviewComponent},
      {path: 'commercial-release-dashboard', component: CommercialReleaseDashboardComponent},
      {path: 'mid', component: MidOverviewComponent},

      {path: 'exemption', component: ExemptionsComponent},
      {path: 'fluxImport', component: FluxSearchImportComponent},
      {path: 'fluxExport', component: FluxSearchExportComponent},
      {path: 'guarantees', component: TransitGuaranteesComponent},
      {path: 'transitTemplates', component: TransitTemplatesComponent},
      {path: 'transitAddressBook', component: TransitAddressBookComponent},
      {path: 'addressbook', component: AddressBookComponent},
      {path: 'releaseToParties', component: ReleaseToPartyOverviewComponent},
      {path: 'licensebook', component: LicenseBookComponent},
      {
        path: 'systemlink',
        component: SystemLinkComponent,
        children: [
          {path: 'monitoring', component: ServiceMonitoringComponent}
        ]
      },
      {path: '**', redirectTo: ''}
    ]
  }
];

const newLookAndFeelModule = [];

if (environment.maintenance) {
  appRoutes.unshift({path: '**', component: MaintenanceComponent})
} else if (AppContext.useNewLookAndFeel()) {
  newLookAndFeelModule.push(PortVisitModule);
}

@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    UserMenuComponent,
    VisitComponent,
    VisitOverviewComponent,
    EmergencyOverviewComponent,
    VisitOverviewItemComponent,
    EmergencyOverviewItemComponent,
    EmergencyOverviewServiceStatusComponent,
    OperationalVisitEditorComponent,
    OperationalPortAuthorityEditorComponent,
    OperationalClearanceFieldComponent,
    OperationalServicesEditorComponent,
    OperationalBerthEditorComponent,
    VisitOverviewServiceStatusComponent,
    TerminalVisitOverviewServiceStatusComponent,
    NewVisitComponent,
    NewsComponent,
    PreferencesComponent,
    VisitDetailsComponent,
    SecurityComponent,
    WasteComponent,
    PassengersComponent,
    PassengerSummaryComponent,
    ShipStoresComponent,
    AuthoriseComponent,
    HarbourDuesComponent,
    HarbourDuesAdminComponent,
    ReductionRotterdamComponent,
    IncidentalComponent,
    FinancialDeclarantComponent,
    NextPortsComponent,
    NextNextPortsComponent,
    VisitHistoryComponent,
    PreviousPortsComponent,
    VisitAdminComponent,
    DangerousGoodsAdminComponent,
    WasteAdminComponent,
    SecurityAdminComponent,
    ShipStoresAdminComponent,
    PassengersAdminComponent,
    EditMovementComponent,
    EditNauticalServiceComponent,
    RegisterAtaComponent,
    RegisterAtdComponent,
    EtaPortAisComponent,
    MessageOverviewComponent,
    ShipToShipActivitiesComponent,
    ConfirmationOfPurchaseComponent,
    NotificationOfDepartureComponent,
    WasteCollectionPdfComponent,
    DeclarationStatusComponent,
    ClearanceStatusComponent,
    ClearanceComponent,
    SspiClearanceComponent,
    EmasClearanceComponent,
    MdohClearanceComponent,
    EmgsClearanceComponent,
    EkhClearanceComponent,
    EkhClearanceStatusComponent,
    DeclarationStatusHealthComponent,
    VisitStatusComponent,
    ReportIssueComponent,
    VesselDetailsComponent,
    DepartureVoyageComponent,
    ArrivalVoyageComponent,
    PlanningHistoryComponent,
    PortDepartureComponent,
    PortArrivalComponent,
    ExitPointComponent,
    NextExitPointComponent,
    HistoryRibbonComponent,
    CargoImportAdminComponent,
    TimelineComponent,
    DefectsComponent,
    InspectionsComponent,
    HealthDeclarationComponent,
    ExchangeAtBerthComponent,
    HealthDeclarationAdminComponent,
    CargoLoadingComponent,
    LoadListComponent,
    LoadingOverlandersComponent,
    LoadContainerComponent,
    ConfirmationModalComponent,
    VesselCommunicationComponent,

    //refdata
    RefdataComponent,
    RefdataUpdateComponent,
    EditBerthComponent,
    EditExemptionComponent,
    EditTerminalComponent,
    EditPortComponent,
    EditPortOfCallComponent,
    EditOrderDeadlinesComponent,
    EditLocationComponent,
    EditPortFacilityComponent,
    EditCarrierComponent,
    EditVesselComponent,
    EditPilotExemptionComponent,
    EditDangerousGoodComponent,
    EditContainerGoodComponent,
    EditSolidGoodComponent,
    EditOilComponent,
    EditGasComponent,
    EditChemicalComponent,
    EditLinerServiceComponent,
    EditSizeTypeComponent,
    EditTerminalEmailComponent,
    EditQuayComponent,
    EditWasteMaterialComponent,
    EditBunkerProductComponent,
    EditBunkerProductSupplierComponent,
    EditBunkerLicenseComponent,
    FluxSearchImportComponent,
    FluxSearchExportComponent,

    //dangerous goods
    DangerousGoodsComponent,
    StowagesComponent,
    HandlingsComponent,
    EditDischargeComponent,
    EditLoadingComponent,
    EditWashingComponent,
    EditInertingComponent,
    EditStowageComponent,
    FumigationDetailsComponent,
    DefumigatorCompanyComponent,
    FumigationStevedoreComponent,
    DangerousGoodsOverviewComponent,
    EditChemicalComponent,
    EditBlendingComponent,
    EditVentilationComponent,
    RegisterAtdPortComponent,
    PortEntryComponent,
    StormPilotageComponent,
    EditRestowComponent,
    CargoImportComponent,
    ImportManifestAdminComponent,
    PartyComponent,
    WarehouseLicenseComponent,
    ConsignmentDetailsComponent,
    ConsignmentsAdminComponent,
    GoodsPlacementComponent,
    GoodsComponent,
    GoodDetailsComponent,
    GoodsDocumentsComponent,
    ConsignmentsComponent,
    ImportContainerComponent,
    ContainersComponent,
    OnCarriageDetailsComponent,
    ImportDeclarationComponent,
    DischargeListComponent,
    DischargeListAdminComponent,
    CargoSummaryComponent,
    AddressBookComponent,
    LicenseBookComponent,
    TransitGuaranteesComponent,
    TransitGuaranteeHistoryComponent,
    PortEntryWarningsComponent,
    OverlandersComponent,
    EntryPointComponent,
    CargoFilterComponent,
    TidalWindowComponent,
    FormattedDateRangeComponent,
    EditDangerInformationComponent,
    PilotBoardingPlaceComponent,
    OrderStatusComponent,
    SelectBerthComponent,
    AgentTransferComponent,

    // Exemptions
    ExemptionGeneralComponent,
    ExemptionIspsComponent,
    ExemptionDebunkeringComponent,
    ExemptionOudeMaasComponent,
    ExemptionTijpoortComponent,
    ExemptionsComponent,
    ExemptionsVesselComponent,
    ExemptionsVesselBargeComponent,
    ExemptionsVesselShipComponent,
    ExemptionBaseComponent,
    Exemption_3_2Component,
    Exemption_3_4Component,
    Exemption_3_5Component,
    Exemption_3_6Component,
    Exemption_3_11Component,
    Exemption_3_13Component,
    Exemption_4_1Component,
    Exemption_4_5Component,
    Exemption_4_6Component,
    Exemption_4_11Component,
    Exemption_4_11bComponent,
    Exemption_4_13_1Component,
    Exemption_4_13_2Component,
    Exemption_5_2Component,
    Exemption_5_3Component,
    Exemption_5_4Component,
    Exemption_5_4_2Component,
    Exemption_6_3_1Component,
    Exemption_6_3_3Component,
    Exemption_6_3_5Component,
    Exemption_6_4Component,
    Exemption_6_5Component,
    Exemption_7_1_1Component,
    Exemption_7_1_2Component,
    Exemption_8_1Component,

    AddRemoveBerthComponent,
    EntryDependencyIconComponent,
    EarliestTimePortEntryComponent,
    PortEntryTabComponent,
    PortEntryCreateComponent,
    PilotStationComponent,
    MovementComponent,
    BerthVisitComponent,
    PortArrivalComponent,
    PortDepartureComponent,
    BerthVisitDetailsComponent,
    BerthVisitTimesComponent,
    ShorePowerComponent,
    ShorePowerDetailsComponent,
    LabeledDateTimeComponent,
    BunkeringEtcComponent,
    BoatmenToBerthSummaryComponent,
    TugboatsSummaryComponent,
    NauticalServicesComponent,
    MovementSummaryComponent,
    MovementDurationSummaryComponent,
    HistoryComponent,
    ActionNavBarComponent,
    NauticalServicesInboundComponent,
    EtaEuEntryComponent,
    MovementBetweenBerthsComponent,
    TimelineDateTimeComponent,
    TimelineIconComponent,
    CargoImportOverviewComponent,
    GeneralInformationComponent,
    WasteAtBerthIconComponent,
    PreviousPortsAndFacilitiesComponent,

    //waste collector
    CollectorSummaryTitleBarComponent,
    CollectorOverviewComponent,
    CollectorSummaryComponent,
    WasteCollectionComponent,
    ItineraryComponent,

    //transit
    TransitOverviewComponent,
    TransitPaginationComponent,
    TransitGoodComponent,
    TransitGoodsComponent,
    TransitDetailsComponent,
    TransitSummaryComponent,
    TransitGuaranteeComponent,
    GuaranteeAdminComponent,
    TransitTemplatesComponent,
    TransitTemplateComponent,
    TransitDeclarationComponent,
    TransitHistoryComponent,
    TransitFilterComponent,
    TransitAddressBookComponent,
    TransitPartyComponent,
    FluxSearchImportComponent,
    FluxSearchExportComponent,
    TransitDeclarationAdminComponent,
    TransitProofOfArrivalComponent,
    TransitCopyContainersFromConsignmentsComponent,
    TransitCopyBulkFromConsignmentsComponent,

    // Transhipment
    TranshipmentOverviewComponent,
    TranshipmentSummaryComponent,
    TranshipmentFilterComponent,
    DeclareTranshipmentOwnConsignmentComponent,
    DeclareTranshipmentContainerConsignmentComponent,
    DeclareTranshipmentBulkConsignmentComponent,
    ViewTranshipmentComponent,
    TranshipmentGoodDetailsComponent,
    TranshipmentGoodsComponent,
    TranshipmentGoodsPlacementComponent,
    TranshipmentHistoryComponent,

    // commercial release
    CommercialReleaseOverviewComponent,
    CommercialReleaseDeclarationComponent,
    CommercialReleaseDetailsComponent,
    CommercialReleaseFilterComponent,
    CommercialReleaseAdminComponent,
    CommercialReleaseSummaryComponent,
    CommercialReleaseDashboardComponent,
    MidOverviewComponent,
    BatchTransitDeclarationComponent,
    TransitDeclarationSendTriggerInfoComponent,
    TransitDeclarationsFromContainersComponent,
    ReleaseToPartyOverviewComponent,

    // Terminal planning
    TerminalPlanningAdminComponent,
    TerminalStatusAdminComponent,
    RegisterTerminalPlanningComponent,
    RegisterTerminalStatusComponent,

    // Port Call Optimization
    PortPassagePlanningAreaComponent,
    EncryptComponent,
    ReleaseToPartyComponent,
    CommercialReleaseHistoryComponent,
    MaintenanceComponent,
    AdminPanelComponent,
    OrganisationSettings,
    EmergencyProcedureSettingsComponent,
    FormComponent,
    MultiBerthComponent,
    ServiceMonitoringComponent,
    SharedChartLegendComponent,
    ServiceMonitoringChartComponent,
    SystemLinkComponent,
    CommercialReleaseEmailPreferencesComponent,
    TerminalPlanningNumberInfoComponent,
    VisitDeclarationStatusComponent,

    // modals
    ModalErrorAutofocus,
    EditAddressComponent,
    EditPilotTransferPointComponent,
    PilotTrajectoryComponent,
    HelmsmenToBerthSummaryComponent,
    EditAddressComponent,

    // pipes
    RequiredQuantityPipe,

    // Harbour dues
    HarbourDuesHistoryComponent,
    HarbourDuesHeaderComponent,
    InfoComponent,
    ExemptionComponent,
    DiscountComponent,
    SpecialTariffComponent,

    // Harbour dues Amsterdam
    HarbourDuesAmsterdamComponent,
    HarbourDuesHistoryAmsterdamComponent,
    HarbourDuesAdminAmsterdamComponent,
    EquipmentListAmsterdamComponent,
    EquipmentCategoryAmsterdamComponent,
    DiscountAmsterdamComponent,
    CargoListAmsterdamComponent,

    // Harbour dues Rotterdam
    HarbourDuesRotterdamComponent,
    HarbourDuesDetailsRotterdamComponent,
    BerthDetailsRotterdamComponent,
    BerthVisitRotterdamComponent,
    BerthVisitCargoRotterdamComponent,
    ContainerHandlingRotterdamComponent,
    BerthVisitContainerRotterdamComponent,
    BerthVisitTabsRotterdamComponent,
    HarbourDuesCostsRotterdamComponent,
    HarbourDuesRotterdamProFormaComponent,
    HarbourDuesRotterdamProFormaInvoiceComponent,
    HarbourDuesRotterdamProFormaInvoiceSkeletonComponent,
    HarbourDuesRotterdamProFormaTableComponent,
    HarbourDuesRotterdamProFormaColumnDirective,
    HarbourDuesRotterdamProFormaValueDirective,
    ReleaseToPartyHistoryComponent
  ],
  exports: [
    EntryDependencyIconComponent,
    ActionNavBarComponent
  ],
  imports: [
    RouterModule.forRoot(appRoutes, {enableTracing: false}),
    CookieModule.forRoot(),
    PortVisitCommonModule,
    ...newLookAndFeelModule,
    BrowserModule,
    CommonModule,
    FormsModule,
    TypeaheadWindowComponent,
    SessionMonitoringModule.withConfig(getSessionMonitoringConfig()),
    CdkDragHandle,
    CdkDropList,
    CdkDrag,
    ReactiveFormsModule,
    CargoWeightAmsterdamComponent,
    NdjsonStatusComponent,
    PopoverComponent,
    HighlightComponent,
    NgOptimizedImage,
    StevedoreVisitOverviewServiceStatusComponent,
    CdkDragPlaceholder,
    MovementIconsComponent,
    RouterLink
  ],
  providers: [
    {provide: HTTP_INTERCEPTORS, useClass: AuthenticationTokenInterceptor, multi: true},
    {provide: ErrorHandler, useClass: SentryErrorHandler},
    NdjsonService,
    TitleCasePipe,
    TimestampPipe,
    CalendarPipe,
    CurrencyPipe,
    DecimalPipe,
    RequiredQuantityPipe,
    CommandGateway,
    QueryGateway,
    EventGateway,
    HardcodedRefdataQueryHandler,
    TravelTimesQueryHandler,
    RefdataQueryHandlerMock,
    BezoekschipQueryHandlerMock,
    FacetsQueryHandlerMock,
    BezoekschipCommandHandlerMock,
    GuaranteeCommandHandlerMock,
    UserHandlerMock,
    MetricsQueryHandlerMock,
    CommercialReleaseDashboardQueryHandlerMock,
    provideHttpClient(withInterceptorsFromDi()),
    AppHandler
  ]
})
export class AppModule {
  constructor(injector: Injector) {
    InjectorProvider.injector = injector;
    injector.get(HardcodedRefdataQueryHandler);
    injector.get(TravelTimesQueryHandler);

    // Mock users for both docker and local setup
    if (!environment.production) {
      injector.get(UserHandlerMock);
    }

    // Mocks
    if (!environment.production && !environment.docker) {
      injector.get(RefdataQueryHandlerMock);
      injector.get(BezoekschipQueryHandlerMock);
      injector.get(FacetsQueryHandlerMock);
      injector.get(BezoekschipCommandHandlerMock)
      injector.get(GuaranteeCommandHandlerMock);
      injector.get(MetricsQueryHandlerMock);
      injector.get(CommercialReleaseDashboardQueryHandlerMock);
      injector.get(AppHandler);
    }
  }
}
